import React, { useCallback, useMemo, useRef, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Checkbox, FormControlLabel, Hidden, Typography } from '@mui/material';
import CmtCard from '@coremat/CmtCard';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import {
  useActiveExteriorSignageQuote,
  useExteriorSignageQuote,
  usePatchApproveExteriorSignageQuote,
  usePatchExteriorSignageQuoteChangeInformation,
  usePostExteriorSignageQuoteCheckout,
} from 'services/Data/ExteriorSignageQuoteService';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import ExteriorSignageQuoteInfoCheckoutSection from './ExteriorSignageQuoteInfoCheckoutSection';
import ExteriorSignageQuoteDeliveryCheckoutSection from './ExteriorSignageQuoteDeliveryCheckoutSection';
import { useHistory } from 'react-router-dom';
import BaseModal from 'Components/BaseModal';
import SimpleReactValidator from 'simple-react-validator';
import { validatorConfig } from 'services/Validators/Validation';
import useToast from 'services/Hooks/useToast';
import useForceUpdate from 'services/Hooks/useForceUpdate';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import { ExteriorSignageQuoteStatusEnumValue } from 'enums/ExteriorSignageQuoteStatusEnum';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import ExteriorSignageQuoteTermConditions from './ExteriorSignageQuoteTermConditions';
import ExteriorSignageQuoteUploadLogoSection from './ExteriorSignageQuoteUploadLogoSection';
import PhoneInput from '@acnb/CustomComponents/PhoneInput/PhoneInput';
import { homePage } from 'config/config';
import StorePageHeader from 'Components/StorePageHeader';

const useStyles = makeStyles(theme => ({
  pageFull: {
    width: '100%',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkoutBtn: {
    marginTop: '1vh',
  },
  deliverySection: {
    position: 'sticky',
    top: '10vh',
  },
  card: {
    overflow: 'visible',
  },
  checkboxError: {
    color: theme.palette.error.main,
    '& .MuiSvgIcon-root': {
      color: theme.palette.error.main,
    },
  },
}));

const ExteriorSignageQuoteCheckout = ({ fromHistory }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const forceUpdate = useForceUpdate();

  const { errorMessage } = useToast();
  const validator = useRef(new SimpleReactValidator(validatorConfig.getConfig(t)));

  const [showCheckoutConfirmed, setShowCheckoutConfirmed] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [isBrandSelectionError, setIsBrandSelectionError] = useState(false);

  const accountReducer = useSelector(state => state.AccountReducer);

  let { exteriorSignageQuoteId } = useParams();

  // active cart is used in cart (it's the current cart being in use)
  // history cart is used in the history only
  const { data: activeExteriorSignageQuote, isLoading: gettingExteriorSignageQuote } = useActiveExteriorSignageQuote(
    accountReducer.isLoggedIn,
  );
  const { data: historyExteriorSignageQuote, isLoading: gettingExteriorSignageQuoteHistory } = useExteriorSignageQuote(
    exteriorSignageQuoteId,
  );
  const { mutate: changeInformations, isPending: changingInformations } = usePatchExteriorSignageQuoteChangeInformation();

  const exteriorSignageQuote = useMemo(
    () => (exteriorSignageQuoteId ? historyExteriorSignageQuote : activeExteriorSignageQuote),
    [exteriorSignageQuoteId, historyExteriorSignageQuote, activeExteriorSignageQuote],
  );

  const { mutate: exteriorSignageQuoteCheckout, isPending: isCheckingOut } = usePostExteriorSignageQuoteCheckout();
  const { mutate: changeExteriorSignageQuoteStatut } = usePatchApproveExteriorSignageQuote();

  const highestNumberOfExteriorSignageBrandSelected = useMemo(() => {
    const numberOfBrands = exteriorSignageQuote?.quoteItems?.map(e => e.numberOfBrand);
    return Math.max(numberOfBrands);
  }, [exteriorSignageQuote?.quoteItems]);

  const currentExteriorSignageBrandSelectedAmount = useMemo(() => {
    const currentExteriorSignage =
      (exteriorSignageQuote?.usesCanAmBrand ? 1 : 0) +
      (exteriorSignageQuote?.usesSeaDooBrand ? 1 : 0) +
      (exteriorSignageQuote?.usesSkiDooBrand ? 1 : 0);

    return currentExteriorSignage;
  }, [exteriorSignageQuote?.usesCanAmBrand, exteriorSignageQuote?.usesSeaDooBrand, exteriorSignageQuote?.usesSkiDooBrand]);

  const handleCheckout = useCallback(() => {
    let brandError = currentExteriorSignageBrandSelectedAmount < highestNumberOfExteriorSignageBrandSelected;
    setIsBrandSelectionError(brandError);

    if (!validator.current.allValid() || brandError) {
      validator.current.showMessages();
      forceUpdate();

      errorMessage({
        message: 'contraint_errors',
      });
      return;
    }

    validator.current.hideMessages();

    exteriorSignageQuoteCheckout(exteriorSignageQuote);
    setShowCheckoutConfirmed(true);
  }, [
    currentExteriorSignageBrandSelectedAmount,
    errorMessage,
    exteriorSignageQuote,
    exteriorSignageQuoteCheckout,
    forceUpdate,
    highestNumberOfExteriorSignageBrandSelected,
  ]);

  const handleCloseCancelExteriorSignageQuoteModal = useCallback(() => {
    setShowCancelModal(false);
  }, [setShowCancelModal]);

  const handleCloseConfirmedCheckoutModal = useCallback(() => {
    setShowCheckoutConfirmed(false);
  }, [setShowCheckoutConfirmed]);

  const handleChangeStatus = useCallback(
    newStatus => {
      changeExteriorSignageQuoteStatut({ ...exteriorSignageQuote, status: newStatus });
    },
    [changeExteriorSignageQuoteStatut, exteriorSignageQuote],
  );

  const isLoading = useMemo(
    () => gettingExteriorSignageQuote || gettingExteriorSignageQuoteHistory || isCheckingOut || changingInformations,
    [changingInformations, gettingExteriorSignageQuote, gettingExteriorSignageQuoteHistory, isCheckingOut],
  );

  const actionButtons = useMemo(
    () => [
      {
        variant: 'contained',
        color: 'secondary',
        hidden: exteriorSignageQuoteId,
        action:
          !exteriorSignageQuoteId &&
          exteriorSignageQuote?.status === ExteriorSignageQuoteStatusEnumValue.Active &&
          handleCheckout,
        label: !exteriorSignageQuoteId && t('proceedCheckoutExteriorSignageQuote'),
        disabled:
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active || exteriorSignageQuote?.isDemo,
      },
      {
        variant: 'contained',
        color: 'error',
        hidden: !exteriorSignageQuoteId,
        action: () => setShowCancelModal(true),
        label: t('cancel'),
        disabled:
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active &&
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Submitted &&
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.PendingQuote &&
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.PendingDealerApproval,
      },
    ],
    [exteriorSignageQuote?.status, exteriorSignageQuoteId, handleCheckout, t, exteriorSignageQuote?.isDemo],
  );

  const backUrl = useMemo(() => (fromHistory ? '/dealer/profile/request-histories' : '/dealer/profile/awaiting-requests'), [
    fromHistory,
  ]);

  return (
    <Box className={classes.pageFull}>
      <BaseModal
        show={showCheckoutConfirmed}
        title={t('exteriorSignageQuoteCheckoutConfirmed')}
        message={t('exteriorSignageQuoteCheckoutConfirmedText')}
        closeBtnMessage={t('close')}
        onClose={handleCloseConfirmedCheckoutModal}
        actions={[
          {
            onClick: () => history.push(homePage),
            color: 'secondary',
            style: { color: 'black', borderRadius: 0 },
            variant: 'contained',
            message: t('goToHome'),
          },
        ]}
      />
      <BaseModal
        show={showCancelModal}
        title={t('cancelExteriorSignageQuote')}
        message={t('cancelExterirorSignageQuoteText')}
        closeBtnMessage={t('close')}
        onClose={handleCloseCancelExteriorSignageQuoteModal}
        actions={[
          {
            onClick: () => {
              handleChangeStatus(ExteriorSignageQuoteStatusEnumValue.Cancelled);
              history.push(backUrl);
            },
            color: 'error',
            style: { color: 'white' },
            variant: 'contained',
            message: t('cancel'),
          },
        ]}
      />
      <Box sx={{ mb: 9 }}>
        <StorePageHeader title={t('quote')} imgLeft backgroundColor="lightGray" borderRadius="0.5rem" />
      </Box>

      <CmtCard className={classes.card}>
        <CmtCardHeader title={''}>
          {exteriorSignageQuoteId && (
            <Button
              onClick={() => history.push(backUrl)}
              variant="outlined"
              sx={{
                borderRadius: 0,
              }}>
              {t('backToList')}
            </Button>
          )}
        </CmtCardHeader>
        <CmtCardContent>
          <GridContainer>
            <GridItem xs={12}>
              <Typography variant="h1">{t('contactUs')}</Typography>
              <Box sx={{ my: 5 }}>
                <Box sx={{ my: 1 }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('exteriorSignageQuoteContactUsBody1', { email: 'enseignes.exterieures@brp.com' }),
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('exteriorSignageQuoteContactUsBody2'),
                    }}
                  />
                </Box>
              </Box>
            </GridItem>
            <GridItem xs={12} md={6}>
              <Box className={classes.deliverySection}>
                <Typography variant="h1">{t('contactInformation')}</Typography>
                <Box sx={{ my: 5 }}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingExteriorSignageQuote}
                        component={
                          exteriorSignageQuote && (
                            <CustomInput
                              labelText={t('contactName')}
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('contactName'),
                                exteriorSignageQuote?.contactName,
                                `required`,
                              )}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...exteriorSignageQuote, contactName: value });
                                },
                                disabled: exteriorSignageQuoteId,
                                defaultValue: exteriorSignageQuote.contactName || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingExteriorSignageQuote}
                        component={
                          exteriorSignageQuote && (
                            <CustomInput
                              labelText={t('email')}
                              id="email"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('email'),
                                exteriorSignageQuote?.contactEmail,
                                `required|email`,
                              )}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...exteriorSignageQuote, contactEmail: value });
                                },
                                disabled: exteriorSignageQuoteId,
                                defaultValue: exteriorSignageQuote.contactEmail || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingExteriorSignageQuote}
                        component={
                          exteriorSignageQuote && (
                            <CustomInput
                              labelText={t('phone')}
                              labelProps={{
                                shrink: isPhoneFocused || exteriorSignageQuote.contactPhoneNumber,
                              }}
                              id="phone"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('phone'),
                                exteriorSignageQuote?.contactPhoneNumber,
                                `required`,
                              )}
                              inputProps={{
                                inputComponent: PhoneInput,
                                onFocus: () => setIsPhoneFocused(true),
                                onBlur: ({ target: { value } }) => {
                                  setIsPhoneFocused(false);

                                  changeInformations({ ...exteriorSignageQuote, contactPhoneNumber: value });
                                },
                                disabled: exteriorSignageQuoteId,
                                defaultValue: exteriorSignageQuote.contactPhoneNumber || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                  </GridContainer>
                </Box>
                <Typography variant="h1">{t('exteriorSignageQuoteBrands')}</Typography>
                <Box sx={{ my: 5 }}>
                  <GridContainer>
                    <GridItem xs={12} md={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingExteriorSignageQuote}
                        component={
                          exteriorSignageQuote && (
                            <GridContainer>
                              {isBrandSelectionError && (
                                <GridItem xs={12}>
                                  <Alert severity="danger">
                                    {t('exteriorSignageBrandErrorAlert', {
                                      count: highestNumberOfExteriorSignageBrandSelected,
                                    })}
                                  </Alert>
                                </GridItem>
                              )}

                              <GridItem xs={12} md={4}>
                                <FormControlLabel
                                  className={isBrandSelectionError ? classes.checkboxError : ''}
                                  control={
                                    <Checkbox
                                      onChange={({ target: { checked } }) =>
                                        changeInformations({ ...exteriorSignageQuote, usesCanAmBrand: checked })
                                      }
                                      checked={exteriorSignageQuote?.usesCanAmBrand || false}
                                      disabled={exteriorSignageQuoteId}
                                    />
                                  }
                                  label={t('usesCanAmBrand')}
                                />
                              </GridItem>

                              <GridItem xs={12} md={4}>
                                <FormControlLabel
                                  className={isBrandSelectionError ? classes.checkboxError : ''}
                                  control={
                                    <Checkbox
                                      onChange={({ target: { checked } }) =>
                                        changeInformations({ ...exteriorSignageQuote, usesSeaDooBrand: checked })
                                      }
                                      checked={exteriorSignageQuote?.usesSeaDooBrand || false}
                                      disabled={exteriorSignageQuoteId}
                                    />
                                  }
                                  label={t('usesSeaDooBrand')}
                                />
                              </GridItem>
                              <GridItem xs={12} md={4}>
                                <FormControlLabel
                                  className={isBrandSelectionError ? classes.checkboxError : ''}
                                  control={
                                    <Checkbox
                                      onChange={({ target: { checked } }) =>
                                        changeInformations({ ...exteriorSignageQuote, usesSkiDooBrand: checked })
                                      }
                                      checked={exteriorSignageQuote?.usesSkiDooBrand || false}
                                      disabled={exteriorSignageQuoteId}
                                    />
                                  }
                                  label={t('usesSkiDooBrand')}
                                />
                              </GridItem>
                              <GridItem xs={12}>
                                <SkeletonWrapper
                                  skeletonProps={{
                                    width: 210,
                                    height: 33,
                                  }}
                                  isLoading={gettingExteriorSignageQuote}
                                  component={
                                    exteriorSignageQuote && (
                                      <CustomInput
                                        labelText={t('oemBrandRequested')}
                                        formControlProps={{
                                          fullWidth: true,
                                        }}
                                        inputProps={{
                                          type: 'text',

                                          onBlur: ({ target: { value } }) => {
                                            changeInformations({ ...exteriorSignageQuote, oemBrandRequested: value });
                                          },
                                          disabled: exteriorSignageQuoteId,
                                          defaultValue: exteriorSignageQuote.oemBrandRequested || '',
                                        }}
                                      />
                                    )
                                  }
                                />
                              </GridItem>
                            </GridContainer>
                          )
                        }
                      />
                    </GridItem>
                  </GridContainer>
                </Box>

                <Typography variant="h1">{t('delivery')}</Typography>
                <ExteriorSignageQuoteDeliveryCheckoutSection
                  exteriorSignageQuote={exteriorSignageQuote}
                  isLoading={isLoading}
                  validator={validator}
                  isDisabled={exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active}
                  actionBtns={actionButtons}
                />
              </Box>
            </GridItem>
            <GridItem xs={12} md={6}>
              <Box>
                <Typography variant="h1">{t('quoteInfo')}</Typography>
                <ExteriorSignageQuoteInfoCheckoutSection
                  validator={validator}
                  exteriorSignageQuote={exteriorSignageQuote}
                  isLoading={isLoading}
                  isDisabled={exteriorSignageQuoteId}
                />
              </Box>
            </GridItem>

            <Hidden mdUp>
              <ExteriorSignageQuoteUploadLogoSection
                exteriorSignageQuote={exteriorSignageQuote}
                isDisabled={exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active}
                isLoading={isLoading}
                validator={validator}
              />
              <GridItem xs={12}>
                <ExteriorSignageQuoteTermConditions />
              </GridItem>
              <GridItem xs={12}>
                {actionButtons
                  .filter(b => !b.hidden)
                  .map(btnInfo => (
                    <Button
                      className={classes.checkoutBtn}
                      sx={{ borderRadius: 0 }}
                      variant={btnInfo.variant}
                      color={btnInfo.color}
                      disabled={btnInfo.disabled}
                      onClick={() => btnInfo.action()}>
                      {btnInfo.label}
                    </Button>
                  ))}
              </GridItem>
            </Hidden>
          </GridContainer>
        </CmtCardContent>
      </CmtCard>
    </Box>
  );
};

export default ExteriorSignageQuoteCheckout;
